import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { db } from "../../api";
import { collection, addDoc, serverTimestamp, onSnapshot, query, where } from "../../api";
import { useAuth } from "../../context/AuthContext";

export default function ReservationForm() {
  const { currentUser } = useAuth();
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const { register, handleSubmit, reset, watch, formState: {isSubmitting} } = useForm();
  const [categories, setCategories] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  
  const watchCategory = watch('category');

  useEffect(() => {
    // Fetch categories
    const unsubCategories = onSnapshot(collection(db, "equipment_categories"), (snapshot) => {
      const categoryList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCategories(categoryList);
    });
    
    return () => unsubCategories();
  }, []);
  
  useEffect(() => {
    if (watchCategory) {
      // Fetch available items for selected category
      const q = query(
        collection(db, "inventory"),
        where("category", "==", watchCategory)
      );
      
      const unsubItems = onSnapshot(q, (snapshot) => {
        const itemList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })).filter(item => item.quantity > 0);
        setAvailableItems(itemList);
      });
      
      return () => unsubItems();
    }
  }, [watchCategory]);
  
  const calculateTotalCost = (startDate, endDate, dailyRate) => {
    if (!startDate || !endDate || !dailyRate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const days = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
    return (days * dailyRate).toFixed(2);
  };

  const onSubmit = async (data) => {
    if (!currentUser) return;
    try {
      const selectedItem = availableItems.find(item => item.id === data.itemId);
      const startDate = new Date(data.startDate);
      const endDate = new Date(data.endDate);
      const totalCost = calculateTotalCost(data.startDate, data.endDate, selectedItem?.dailyRate || 0);
      
      await addDoc(collection(db, "reservations"), {
        userId: currentUser.uid,
        requestId,
        itemId: data.itemId,
        itemName: selectedItem?.name,
        category: data.category,
        size: data.size,
        startDate: startDate,
        endDate: endDate,
        totalCost: parseFloat(totalCost),
        dailyRate: selectedItem?.dailyRate || 0,
        status: 'pending',
        createdAt: serverTimestamp(),
        customerNotes: data.customerNotes || ''
      });
      alert("Reservation submitted successfully!");
      reset();
      setRequestId(crypto.randomUUID());
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-customWhite flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded shadow p-6">
        <p>Rates are estimates before tax. Availability is checked for your dates when submitted. The return date is not charged; same-day rentals have a one-day minimum.</p>
        <h2 className="text-burgundy text-2xl font-semibold mb-6">
          Reserve Equipment
        </h2>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col space-y-4"
        >
          <div className="flex flex-col">
            <label className="text-sm font-medium text-navy">
              Equipment Category:
            </label>
            <select
              {...register("category", { required: true })}
              className="border border-gray-300 rounded p-2 focus:outline-none focus:border-burgundy"
              required
            >
              <option value="">Select a category</option>
              {categories.map((category) => (
                <option key={category.id} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          
          {watchCategory && availableItems.length > 0 && (
            <div className="flex flex-col">
              <label className="text-sm font-medium text-navy">
                Select Item:
              </label>
              <select
                {...register("itemId", { required: true })}
                className="border border-gray-300 rounded p-2 focus:outline-none focus:border-burgundy"
                required
              >
                <option value="">Select an item</option>
                {availableItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name} {item.size ? `(Size: ${item.size})` : ''} - ${item.dailyRate || 0}/day
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {watchCategory && availableItems.length === 0 && (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
              No items available in this category. Please contact us for availability.
            </div>
          )}
          
          <div className="flex flex-col">
            <label className="text-sm font-medium text-navy">
              Start Date:
            </label>
            <input
              type="date"
              {...register("startDate")}
              min={new Date().toISOString().split('T')[0]}
              required
              className="border border-gray-300 rounded p-2 focus:outline-none focus:border-burgundy"
            />
          </div>
          
          <div className="flex flex-col">
            <label className="text-sm font-medium text-navy">
              End Date:
            </label>
            <input
              type="date"
              {...register("endDate")}
              min={new Date().toISOString().split('T')[0]}
              required
              className="border border-gray-300 rounded p-2 focus:outline-none focus:border-burgundy"
            />
          </div>
          
          <div className="flex flex-col">
            <label className="text-sm font-medium text-navy">
              Additional Notes (Optional):
            </label>
            <textarea
              {...register("customerNotes")}
              placeholder="Any special requests or information"
              rows="3"
              className="border border-gray-300 rounded p-2 focus:outline-none focus:border-burgundy"
            />
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-burgundy text-white py-2 rounded hover:bg-opacity-90 transition"
          >
            Submit Reservation
          </button>
        </form>
      </div>
    </div>
  );
}
